import { Link } from 'react-router-dom';
import { V2Badge, V2Card, V2CardHeader, LoadingState } from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import type { OpenItemListResponse } from '../../lib/types/finance.js';
import {
  formatRialFa,
  matchingSourceReference,
  OPEN_ITEM_STATUS_TONE,
  openItemHref,
} from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';

/**
 * Cross-module AR/AP evidence on a sales or purchase order. Destination
 * pages re-check permission; a 403 here only hides the list, it is not a
 * grant.
 */
export function RelatedOpenItemsCard({
  kind,
  sourceReference,
  base,
}: {
  readonly kind: 'ar' | 'ap';
  readonly sourceReference: string;
  readonly base: string;
}) {
  const { unit, label } = useAmountDisplay();
  const result = useApiGet<OpenItemListResponse>(
    sourceReference ? `${base}/api/v1/finance/${kind}/open-items` : null,
    [base, kind, sourceReference],
  );
  const title = kind === 'ar' ? 'وضعیت دریافتنی' : 'وضعیت پرداختنی';
  const listHref = kind === 'ar' ? `${base}/finance/ar` : `${base}/finance/ap`;
  const denied =
    kind === 'ar'
      ? 'مشاهدهٔ دریافتنی این سفارش نیازمند دسترسی مالی است.'
      : 'مشاهدهٔ پرداختنی این سفارش نیازمند دسترسی مالی است.';
  const empty = kind === 'ar' ? 'قلم دریافتنی مرتبطی ثبت نشده.' : 'قلم پرداختنی مرتبطی ثبت نشده.';

  const matches =
    result.status === 'ready' ? matchingSourceReference(result.data.items, sourceReference) : [];

  return (
    <V2Card>
      <V2CardHeader
        title={title}
        actions={
          <Link to={listHref} style={{ fontSize: 13 }}>
            دفتر مالی
          </Link>
        }
      />
      {result.status === 'loading' ? <LoadingState /> : null}
      {result.status === 'error' ? (
        <div style={{ padding: '16px 20px', fontSize: 13, color: 'var(--sk-fg-muted)' }}>
          {result.error.status === 403 ? denied : 'وضعیت مالی قابل نمایش نیست.'}
        </div>
      ) : null}
      {result.status === 'ready' ? (
        matches.length === 0 ? (
          <div style={{ padding: '16px 20px', fontSize: 13, color: 'var(--sk-fg-muted)' }}>
            {empty}
          </div>
        ) : (
          <div className="sk-table-scroll">
            <table className="sk-table sk-table--compact">
              <thead>
                <tr>
                  <th>مانده ({label})</th>
                  <th>وضعیت</th>
                  <th>سررسید</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {matches.map((item) => (
                  <tr key={item.openItemId}>
                    <td className="sk-num">{formatRialFa(item.openBalanceRial, unit)}</td>
                    <td>
                      <V2Badge tone={OPEN_ITEM_STATUS_TONE[item.status] ?? 'neutral'}>
                        {item.statusLabel}
                      </V2Badge>
                    </td>
                    <td className="sk-num">{item.dueDate}</td>
                    <td>
                      <Link to={openItemHref(base, kind, item.openItemId)}>جزئیات</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </V2Card>
  );
}
